import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { flushPendingEmails } from "../lib/emailjs";
import { Bell, LogOut, Menu, X } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger
} from "./ui/dropdown-menu";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { BrandLogo } from "./BrandLogo";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [notifs, setNotifs] = useState([]);
  const [open, setOpen] = useState(false);
  const [contact, setContact] = useState({});

  useEffect(() => {
    api.get("/settings/contact").then((r) => setContact(r.data || {})).catch(() => {});
  }, []);

  const fetchNotifs = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotifs(data);
    } catch (e) {
      console.warn("[Layout] fetchNotifs failed:", e?.response?.status || e?.message);
    }
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    fetchNotifs();
    flushPendingEmails(api, user);
    const t = setInterval(() => { fetchNotifs(); flushPendingEmails(api, user); }, 30000);
    return () => clearInterval(t);
  }, [user, location.pathname, fetchNotifs]);

  const unread = notifs.filter((n) => !n.read).length;

  const markAllRead = async () => {
    await api.post("/notifications/read-all");
    fetchNotifs();
  };

  const handleNotifClick = async (n) => {
    // Mark single notification read (best-effort, never block navigation on this)
    try {
      await api.post(`/notifications/${n.id}/read`);
    } catch (e) {
      // Read-marking is non-critical UI sugar — log at debug level only so the
      // browser console doesn't get noisy when offline or rate-limited.
      console.debug("[Layout] mark-read failed:", e?.response?.status || e?.message);
    }
    const t = n.notification_type;
    const rid = n.related_id;
    const isAdmin = user?.role === "admin" || user?.role === "super_admin";

    // Admin-facing notification types — route to the admin destination
    if (isAdmin) {
      // New Listing Submitted → admin review queue (Requests tab) so the listing
      // shows up with Approve / Deny actions inline. Optional listing detail link
      // is shown right there in the row.
      if (t === "listing_review") {
        navigate(`/dashboard?tab=requests${rid ? `&listing=${rid}` : ""}`);
        fetchNotifs();
        return;
      }
      if (t === "application_review") {
        navigate(`/dashboard?tab=applications${rid ? `&app=${rid}` : ""}`);
        fetchNotifs();
        return;
      }
      if (t === "new_seeker_request") {
        navigate(`/dashboard?tab=applicants${rid ? `&intake=${rid}` : ""}`);
        fetchNotifs();
        return;
      }
      if (t === "kess_request") {
        navigate(`/dashboard?tab=kess${rid ? `&req=${rid}` : ""}`);
        fetchNotifs();
        return;
      }
    }

    // Provider-/User-facing notification types
    if (t === "application_approved" || t === "application_denied" || t === "application_more_info") {
      navigate("/provider/application");
      fetchNotifs();
      return;
    }
    if (t === "listing_approval" || t === "listing_denied") {
      navigate(rid ? `/listings/${rid}` : "/dashboard");
      fetchNotifs();
      return;
    }
    if (t === "new_referral" || t === "referral_update") {
      navigate(`/dashboard?tab=referrals${rid ? `&ref=${rid}` : ""}`);
      fetchNotifs();
      return;
    }
    if (t === "vacancy") {
      navigate(rid ? `/listings/${rid}` : "/search");
      fetchNotifs();
      return;
    }
    if (t === "kess_update") {
      navigate("/kess-services");
      fetchNotifs();
      return;
    }
    if (t === "verification") {
      // Status update on user's own account — send them to dashboard, which renders
      // the role-appropriate view (admin console for admins, provider/seeker dashboard otherwise).
      navigate("/dashboard");
      fetchNotifs();
      return;
    }
    if (t === "payment" || t === "billing") {
      navigate(isAdmin ? "/dashboard?tab=payments" : "/dashboard");
      fetchNotifs();
      return;
    }

    // Fallback — admin always goes to the admin console (NOT the public home page),
    // everyone else goes to their role dashboard. Never route to "/" from a click.
    navigate("/dashboard");
    fetchNotifs();
  };

  const navLinks = [
    { to: "/search", label: "Get Connected" },
    { to: "/for-providers", label: "For Providers" },
    { to: "/for-agencies", label: "For Agencies" },
    { to: "/kess", label: "Kess Services" },
    { to: "/pricing", label: "Pricing" },
    { to: "/about", label: "About" },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-[#F5F5F0]">
      <header className="border-b border-[#D1D5DB] bg-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 h-16 flex items-center justify-between relative">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-2.5">
            <span className="w-9 h-9 rounded-sm bg-white border border-[#D1D5DB] flex items-center justify-center overflow-hidden">
              <BrandLogo className="w-7 h-7 object-contain" />
            </span>
            <div className="leading-tight">
              <div className="font-heading font-bold text-[#0A192F] text-sm tracking-tight">SAFE HARBOR</div>
              <div className="text-[10px] tracking-[0.2em] text-[#C05621] font-semibold uppercase">Veterans</div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((l) => (
              <Link key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase().replace(/\s/g,'-')}`}
                className={`text-sm font-medium hover:text-[#1E3A8A] transition-colors ${location.pathname === l.to ? "text-[#1E3A8A]" : "text-[#4A5568]"}`}>
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button data-testid="notifications-trigger" className="relative p-2 hover:bg-[#F5F5F0] rounded-md">
                      <Bell className="w-5 h-5 text-[#4A5568]" />
                      {unread > 0 && (
                        <span data-testid="notif-badge" className="absolute -top-0.5 -right-0.5 bg-[#C05621] text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">{unread}</span>
                      )}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-80" align="end">
                    <DropdownMenuLabel className="flex items-center justify-between">
                      Notifications
                      {unread > 0 && <button onClick={markAllRead} data-testid="mark-all-read" className="text-xs text-[#1E3A8A] font-medium">Mark all read</button>}
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {notifs.length === 0 && <div className="p-4 text-sm text-[#4A5568]">No notifications yet.</div>}
                    <div className="max-h-96 overflow-y-auto" data-testid="notif-list">
                      {notifs.map((n) => (
                        <DropdownMenuItem key={n.id} onSelect={(e) => { e.preventDefault(); handleNotifClick(n); }} className="flex-col items-start py-2.5 cursor-pointer" data-testid={`notif-${n.id}`}>
                          <div className="flex items-center gap-2 w-full">
                            <span className="text-sm font-medium text-[#0A192F]">{n.title}</span>
                            {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-[#C05621]" />}
                          </div>
                          <span className="text-xs text-[#4A5568]">{n.message}</span>
                        </DropdownMenuItem>
                      ))}
                    </div>
                    {notifs.length > 0 && (
                      <div className="border-t border-[#D1D5DB] px-3 py-2 text-[11px] text-[#4A5568] flex items-center justify-between" data-testid="notif-footer">
                        <span>Showing {notifs.length} most recent</span>
                        {unread > 0 && <span className="text-[#C05621] font-medium">{unread} unread</span>}
                      </div>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button data-testid="user-menu-trigger" className="flex items-center gap-2 px-3 py-1.5 rounded-md hover:bg-[#F5F5F0]">
                      <div className="w-7 h-7 rounded-full bg-[#1E3A8A] text-white text-xs font-bold flex items-center justify-center">
                        {(user.full_name || user.email).slice(0,1).toUpperCase()}
                      </div>
                      <span className="text-sm font-medium text-[#0A192F] max-w-[120px] truncate">{user.full_name}</span>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel>
                      <div>{user.full_name}</div>
                      <div className="text-xs font-normal text-[#4A5568]">{user.email}</div>
                      <Badge variant="outline" className="mt-1 text-[10px]">{user.role.replace('_',' ')}</Badge>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard">Dashboard</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/pricing")} data-testid="menu-pricing">Plans & Billing</DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => { logout(); navigate("/"); }} data-testid="menu-logout">
                      <LogOut className="w-4 h-4 mr-2" /> Sign out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login">
                  <Button variant="ghost" className="text-[#0A192F]">Log in</Button>
                </Link>
                <Link to="/register" data-testid="nav-register">
                  <Button className="bg-[#1E3A8A] hover:bg-[#1A365D] text-white rounded-md">Get Started</Button>
                </Link>
              </>
            )}
          </div>

          <button className="md:hidden p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
        {open && (
          <div className="md:hidden absolute left-0 right-0 top-16 border-t border-[#D1D5DB] bg-white px-6 py-4 space-y-3 shadow-lg z-50">
            {navLinks.map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="block text-sm font-medium text-[#0A192F]">{l.label}</Link>
            ))}
            {user ? (
              <>
                <Link to="/dashboard" onClick={() => setOpen(false)} className="block text-sm font-medium text-[#1E3A8A]">Dashboard</Link>
                <button onClick={() => { logout(); navigate("/"); setOpen(false); }} className="text-sm text-[#E53E3E]">Sign out</button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="block text-sm font-medium">Log in</Link>
                <Link to="/register" onClick={() => setOpen(false)} className="block text-sm font-medium text-[#1E3A8A]">Get Started</Link>
              </>
            )}
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t border-[#D1D5DB] bg-white mt-16">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-12 grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="md:col-span-2">
            <div className="font-heading font-bold text-[#0A192F]">SAFE HARBOR VETERANS</div>
            <p className="text-sm text-[#4A5568] mt-2 max-w-sm">A housing placement and referral network connecting housing seekers — veterans, seniors, returning citizens, families in crisis, and people in recovery — with verified supportive housing.</p>
            <p className="text-xs text-[#4A5568] mt-4">Launching in North Carolina. Built for all 50 states.</p>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-[#0A192F] font-semibold mb-3">Platform</div>
            <ul className="space-y-1.5 text-sm text-[#4A5568]">
              <li><Link to="/search">Get Connected</Link></li>
              <li><Link to="/for-providers">For Providers</Link></li>
              <li><Link to="/for-agencies">For Agencies</Link></li>
              <li><Link to="/pricing">Pricing</Link></li>
              <li><Link to="/about">About</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-[#0A192F] font-semibold mb-3">Contact</div>
            <ul className="space-y-1.5 text-sm text-[#4A5568]">
              {contact.phone && <li data-testid="footer-phone">Phone: {contact.phone}</li>}
              {contact.fax && <li data-testid="footer-fax">Fax: {contact.fax}</li>}
              {contact.email && <li><a href={`mailto:${contact.email}`} className="hover:text-[#1E3A8A]" data-testid="footer-email">{contact.email}</a></li>}
              {contact.facebook && <li><a href={contact.facebook} target="_blank" rel="noreferrer" className="hover:text-[#1E3A8A]" data-testid="footer-facebook">Facebook</a></li>}
            </ul>
            <div className="text-xs uppercase tracking-widest text-[#0A192F] font-semibold mt-4 mb-1.5">Crisis Lines</div>
            <ul className="text-xs text-[#4A5568] space-y-1">
              <li>Veterans Crisis: 988, press 1</li>
              <li>Homeless Vets: 1-877-424-3838</li>
            </ul>
          </div>
        </div>
        <div className="border-t border-[#D1D5DB] py-4 px-6 text-xs text-[#4A5568] text-center">
          © 2026 Safe Harbor Veterans · A placement and referral network for housing seekers, providers, and agencies.
        </div>
      </footer>
    </div>
  );
}
